<section class="fire">
  <h2>FIRE</h2>

  <p class="error" *ngIf="error" role="alert">{{ error }}</p>
  <p class="loading" *ngIf="loading && !error">Loading…</p>

  <ng-container *ngIf="!loading && !error">
    <div class="inputs">
      <label>
        <span>Invested today</span>
        <input type="text" inputmode="decimal" [(ngModel)]="draft.portfolio"
               (input)="markStale()" (change)="commit()"/>
      </label>

      <label>
        <span>Contributing monthly</span>
        <input type="text" inputmode="decimal" [(ngModel)]="draft.contrib"
               (input)="markStale()" (change)="commit()"/>
      </label>

      <label>
        <span>Real return {{ ratePercent }}</span>
        <input type="range" min="0" max="1500" step="25"
               [(ngModel)]="settings.rateBasisPoints"
               (input)="markStale()" (change)="commit()"/>
        <em>inflation-adjusted, so every date below is in today’s dollars</em>
      </label>

      <label>
        <span>Withdrawal rate {{ swrPercent }}</span>
        <input type="range" [min]="swrMin" [max]="swrMax" [step]="swrStep"
               [(ngModel)]="settings.swrBasisPoints"
               (input)="markStale()" (change)="commit()"/>
      </label>

      <label>
        <span>Age now</span>
        <input type="number" min="0" max="120" step="1"
               [(ngModel)]="settings.currentAge"
               (input)="markStale()" (change)="commit()"/>
      </label>

      <label>
        <span>Coast to age</span>
        <input type="number" min="0" max="120" step="1"
               [(ngModel)]="settings.coastAge"
               (input)="markStale()" (change)="commit()"/>
      </label>
    </div>

    <div class="chips" *ngIf="chips.length">
      <span>Your actual monthly net:</span>
      <button type="button" *ngFor="let c of chips" (click)="useChip(c)">
        {{ c.label }} {{ fmt(c.monthlyCents) }}
      </button>
    </div>

    <fieldset class="spend">
      <legend>Spending the nest egg must cover</legend>

      <label class="radio">
        <input type="radio" name="spendSource" value="ledger"
               [(ngModel)]="settings.spendSource" (change)="commit()"/>
        <span>From my ledger —
          <strong *ngIf="spend && spend.ledgerCents !== null">
            {{ fmt(spend.ledgerCents) }}/yr</strong>
          <em *ngIf="spend && spend.ledgerCents === null">
            no spending recorded in the last 12 complete months</em>
        </span>
      </label>

      <label class="radio">
        <input type="radio" name="spendSource" value="manual"
               [(ngModel)]="settings.spendSource" (change)="commit()"/>
        <span>Enter it myself</span>
        <input type="text" inputmode="decimal" [(ngModel)]="draft.manualSpend"
               [disabled]="settings.spendSource !== 'manual'"
               (input)="markStale()" (change)="commit()"/>
      </label>

      <label class="check">
        <input type="checkbox" [(ngModel)]="settings.deductMortgagePI"
               (change)="commit()"/>
        <span>
          Less the mortgage —
          <ng-container *ngIf="spend">{{ fmt(spend.mortgageDeductionCents) }}/yr</ng-container>
          of principal &amp; interest that stops at payoff.
          <em>Only right if the payment is in your imported ledger. Escrow stays
              in: tax and insurance don’t stop.</em>
        </span>
      </label>

      <p class="total" *ngIf="spend && !deductionCoversAllSpend">
        Perpetual spend <strong>{{ fmt(spend.totalCents) }}</strong>/yr
      </p>
      <p class="total note" *ngIf="spend && deductionCoversAllSpend">
        The mortgage deduction covers
        <ng-container *ngIf="settings.spendSource === 'manual'">the figure you entered</ng-container>
        <ng-container *ngIf="settings.spendSource !== 'manual'">your recorded spending</ng-container>,
        so perpetual spend floors at <strong>{{ fmt(spend.totalCents) }}</strong>/yr.
      </p>
    </fieldset>

    <div class="recalc">
      <button type="button" class="go" [class.stale]="stale" (click)="recalculate()">
        Recalculate
      </button>
      <em *ngIf="stale" role="status">
        Figures below are from your previous numbers.
      </em>
    </div>

    <ng-container *ngIf="hasResults && active as result">
      <dl class="figures">
        <div>
          <dt>Your number</dt>
          <dd>{{ fmt(fireNumberCents) }}</dd>
        </div>
        <div>
          <dt>FI date</dt>
          <dd *ngIf="result.fireMonth !== null">
            {{ fireDate(result) + 'T00:00:00' | date: 'MMM yyyy' }}
          </dd>
          <dd *ngIf="result.fireMonth === null" class="none">not within 60 years</dd>
        </div>
        <div *ngIf="coastCents !== null">
          <dt>Coast FIRE</dt>
          <dd>{{ fmt(coastCents) }}</dd>
        </div>
      </dl>

      <p class="coasted" *ngIf="hasCoasted">
        You’ve already coasted — stop contributing today and you still land at
        {{ boundedCoastAge }}.
      </p>

      <p class="warn" *ngIf="result.loanStalled" role="alert">
        The mortgage payment doesn’t cover its interest, so the balance never
        falls and your target never comes down. Check the Mortgage screen.
      </p>

      <p class="verdict">{{ verdict }}</p>
      <em class="disclosure">
        Pre-tax, and races a real, inflation-adjusted return against the
        mortgage’s nominal rate — a close result should not be read as
        decisive.
      </em>

      <dl class="head-to-head" *ngIf="comparison as cmp">
        <div>
          <dt>Extra → mortgage</dt>
          <dd *ngIf="cmp.toMortgage.fireMonth !== null">
            {{ fireDate(cmp.toMortgage) + 'T00:00:00' | date: 'MMM yyyy' }}
          </dd>
          <dd *ngIf="cmp.toMortgage.fireMonth === null" class="none">not within 60 years</dd>
        </div>
        <div>
          <dt>Extra → invest</dt>
          <dd *ngIf="cmp.toInvest.fireMonth !== null">
            {{ fireDate(cmp.toInvest) + 'T00:00:00' | date: 'MMM yyyy' }}
          </dd>
          <dd *ngIf="cmp.toInvest.fireMonth === null" class="none">not within 60 years</dd>
        </div>
      </dl>

      <div class="scenario" role="group" aria-label="Scenario plotted">
        <button type="button" [class.on]="scenario === 'mortgage'"
                [attr.aria-pressed]="scenario === 'mortgage'"
                (click)="setScenario('mortgage')">Extra → mortgage</button>
        <button type="button" [class.on]="scenario === 'invest'"
                [attr.aria-pressed]="scenario === 'invest'"
                (click)="setScenario('invest')">Extra → invest</button>
      </div>

      <mm-line [points]="portfolioPoints"
               [displayValues]="portfolioDisplay"
               [compare]="targetPoints"
               [compareDisplayValues]="targetDisplay"
               compareLabel="Target"
               ariaLabel="Portfolio"></mm-line>
    </ng-container>
  </ng-container>
</section>
